import React from "react";
import {createPortal} from "react-dom";
import {ArrowBigDown, ArrowBigUp, ArrowUp, Bookmark, LayoutList, MessageSquareText, Share} from "lucide-react";
import {CtaButton, CtaToggle, VoteContainer} from "@/main/react/buttons/CtaButton";
import {NavigationMenuDropdown, type NavigationMenuItem} from "@/main/react/buttons/NavigationMenuDropdown";
import {SectionCountBadge, type SectionDef} from "@/main/react/discussion/DiscussionPageSectionControls";
import {type ContextualAccountAccessDialogContext} from "@/main/react/account_access/AccountAccessDialogContext";
import {Localizer} from "@/main/react/Types";
import {useIsMobileViewport} from "@/main/react/hooks/useIsMobileViewport";
import {useKeyboardInsetPx} from "@/hooks/use-keyboard-inset";

/**
 * Mobile-only, full-width action bar pinned to the bottom edge of the discussion view. It mirrors the
 * post's primary engagement affordances so they stay thumb-reachable while the reader scrolls the whole
 * thread. Left → right: sections · bookmark · share · ▲▼ votes · thought count (count right-most).
 *
 * <p>The SECTIONS control is `DISCUSSION-26` / `DISCUSSION-41` (wave-12 sheet Q4, option A,
 * 2026-08-24). Below `md` the page's section strip is a plain block that scrolls away with the article
 * and never returns, and this bar is the only chrome that stays — so the bar offers the sections
 * itself, and that dropdown is now the route to the conversation.
 *
 * <p><b>The count button opens the COMPOSER (operator request, 2026-09-01).</b> `DISCUSSION-41` also
 * re-pointed it at the conversations section, on the reasoning that it was the only control that
 * LOOKED like a route to the conversation — but the sections dropdown added in the same change is a
 * better route, and with the read path covered the count button is free to be the write path again.
 * That is what a reader at the bottom of a long thread on a phone actually wants: a thumb-reachable way
 * to reply without scrolling back up. It opens `showThoughtTextEditorDialog` (the same composer the
 * desktop count button and the floating action button open) and emits `thought_composer_opened` with
 * `surface=discussion_mobile_bar`, so the three doors stay separable in the funnel.
 *
 * Rendered by {@link DiscussionButtons} (which owns the live vote/bookmark/share state + handlers) only
 * on the full discussion page, and portaled to `document.body` so a transformed ancestor can never clip
 * or mis-anchor the fixed element. It replaces the retired `FloatingDiscussionControls` grouped pill.
 * See designs/04-live/2026-06-16-app-level-floating-action-buttons-design.md (2026-07-20 update).
 */

/**
 * True while any dialog/sheet is open. Every dialog toggles `body.modal-open` (Dialog.tsx); on mobile
 * the argument editor opens as a bottom sheet, so we hide the bar while it is up rather than letting the
 * two stack on the bottom edge.
 */
function useBodyModalOpen(): boolean {
    const [open, setOpen] = React.useState<boolean>(
        () => typeof document !== "undefined" && document.body.classList.contains("modal-open"),
    );

    React.useEffect(() => {
        if (typeof document === "undefined") {
            return;
        }
        const read = () => setOpen(document.body.classList.contains("modal-open"));
        read();
        const observer = new MutationObserver(read);
        observer.observe(document.body, {attributes: true, attributeFilter: ["class"]});
        return () => observer.disconnect();
    }, []);

    return open;
}

/**
 * Reserve space at the bottom of the page for the fixed bar so the last content/arguments are never
 * hidden behind it. Applied while the bar can appear (mobile), cleared on unmount / desktop.
 */
function useBottomBarClearance(active: boolean): void {
    React.useEffect(() => {
        if (!active || typeof document === "undefined") {
            return;
        }
        const previous = document.body.style.paddingBottom;
        document.body.style.paddingBottom = "calc(4.5rem + env(safe-area-inset-bottom))";
        return () => {
            document.body.style.paddingBottom = previous;
        };
    }, [active]);
}

/**
 * The section list the bar offers, supplied by the page rather than rebuilt here (`DISCUSSION-26`).
 *
 * <p>`sections` comes from `buildDiscussionSectionDefs`, the SAME function the strip renders from, so the
 * phone and the desktop cannot come to disagree about which sections exist. `buildHref` is the page's own
 * `buildSectionHref`, so the address a long-press offers is by construction the address a tap produces —
 * and `onSelect` is the page's `onSectionChanged`, so a plain tap goes through the page's history-backed
 * state exactly as the strip's anchors do (rule 33: nothing here writes history).
 */
export type DiscussionMobileSectionNavigation = {
    /**
     * `SectionDef`-shaped, and the `count` is the point (`DISCUSSION-55`).
     *
     * <p>This used to be typed `{ value; label; icon }`, which dropped the per-section count **at the
     * type boundary** — the page has always passed whole `SectionDef`s here, built by the same
     * `buildDiscussionSectionDefs` the strip renders badges from, so the number was thrown away by the
     * declaration rather than by the data. The phone was the version of the section list with the least
     * information, on the one viewport where the strip has scrolled away.
     */
    sections: SectionDef[];
    activeSection?: string;
    buildHref?: (section: string) => string | undefined;
    onSelect: (section: string) => void;
    /**
     * `DISCUSSION-62`, first half. The article is a DESTINATION on this page even though it is not a
     * "section": a reader twenty screens into a 23,410 CSS px thread has no way back to the claim being
     * argued about, because the only chrome that survives the scroll is this bar and its menu offers
     * only the sections. Optional — a caller that cannot scroll gets today's menu exactly.
     *
     * <p>The scroll itself belongs to the page, not to the bar: this component knows nothing about the
     * article's position, and a `window.scrollTo` written here would be the bar reaching into a document
     * it does not own.
     */
    onBackToArticle?: () => void;
    /** The `<nav>`'s existing accessible name, reused verbatim — no new copy (rule 11). */
    ariaLabel: string;
};

export type DiscussionMobileActionBarProps = {
    localizer: Localizer;

    /**
     * `DISCUSSION-26`. Optional: the bar is also rendered by callers that have no section state to
     * offer, and a bar without the control is exactly the behaviour that shipped before this row.
     */
    sectionNavigation?: DiscussionMobileSectionNavigation;

    // Thought count (right-most).
    showComment: boolean;
    argumentsCount: number;
    commentDisabled: boolean;
    commentDisabledReason: string | null;
    onComment: () => void;
    /**
     * Already-localized label for the count button. Passed in rather than looked up here because
     * `DiscussionButtons` picks the wording, and the desktop FAB, the desktop count button and this bar
     * must all describe the same click identically. Required on purpose: the only renderer is
     * `DiscussionButtons`, so there is no call site to keep compiling, and a defaulted label is a label
     * that can silently go stale.
     *
     * <p>`DISCUSSION-41` (2026-08-24) pointed this button at the conversations section and labelled it
     * with the unit's name. Since the operator's 2026-09-01 request it opens the COMPOSER again (see the
     * component docstring), so `DiscussionButtons` passes the write verb, and the label must keep
     * describing that click.
     */
    commentTitle: string;

    // Votes.
    agreementsCount: number;
    disagreementsCount: number;
    isAgreeing: boolean;
    isDisagreeing: boolean;
    votesDisabled: boolean;
    votesDisabledReason: string | null;
    agreementTitle: string;
    disagreementTitle: string;
    reactAccountAccessDialog: ContextualAccountAccessDialogContext;
    onAgree: () => void;
    onDisagree: () => void;

    // Share. `showShare` is false on a deleted post, for the same reason `showBookmark` is: the bar
    // must not hand out the public URL of content its author has taken down, and the share path fires
    // `trackShare` for it (EMPTY-STATES-24, 2026-08-14). Required rather than defaulted — the only
    // renderer is `DiscussionButtons`, and a defaulted `true` here is exactly how the guard on the
    // desktop row would come to disagree with the guard on the phone.
    showShare: boolean;
    onShare: () => void;
    /**
     * `DISCUSSION-58` — true between the tap and whatever the share path produces (an OS sheet, the
     * drawer chunk landing, a clipboard write). It is the PENDING half of that row: the fallback path
     * awaits a dynamic import before anything appears on screen, and with no signal on the trigger a
     * member's natural response to that gap is a second tap. `CtaButton` treats `isLoading` as
     * disabled too, so this removes the second tap rather than handling it.
     *
     * <p>Optional and defaulting to false: a caller that has not threaded it gets exactly today's
     * behaviour.
     */
    sharePending?: boolean;

    // Bookmark (left-most).
    showBookmark: boolean;
    isSavedToBookmarks: boolean;
    savingToBookmarks: boolean;
    saveAccountAccessDialog: ContextualAccountAccessDialogContext;
    onToggleBookmark: () => void;
};

const stopEvent = (ev?: {preventDefault?: () => void; stopPropagation?: () => void}) => {
    try {
        ev?.preventDefault?.();
        ev?.stopPropagation?.();
    } catch {
        // ignore — synthetic events without these methods
    }
};

export default function DiscussionMobileActionBar(props: DiscussionMobileActionBarProps) {
    const {localizer, sectionNavigation} = props;
    const isMobile = useIsMobileViewport();
    const modalOpen = useBodyModalOpen();
    // The soft keyboard's overlap, used here as a PREDICATE rather than as an offset. The step-aside
    // comment below says why this bar hides where its siblings ride up.
    const keyboardInsetPx = useKeyboardInsetPx();
    useBottomBarClearance(isMobile);

    // The section already on screen is offered rather than dropped, so the menu's membership does not
    // change as the reader moves — which is what makes it navigable by position. It is now MARKED as
    // the one you are in, the same statement `aria-current="page"` makes on the strip.
    //
    // ✅ `DISCUSSION-51` / `-55` / `-67` / `-74`, 2026-09-07. All four were one root cause: page
    // navigation was being expressed with `CtaButtonDropdown`, the `…` CARD-OVERFLOW primitive, whose
    // items are one-shot actions — so none of them could be "the current one", carry a quantity, or be
    // an address. Wave-19 Q6 answered it with a new named navigation component rather than by growing
    // the action menu with navigation concepts it would then carry into every `…` in the product, and
    // this is the one call site that moves. `NavigationMenuDropdown` renders `aria-current` plus a
    // non-colour cue, a trailing slot, and a real `<a href>` per row.
    //
    // The trailing badge is the STRIP's own `SectionCountBadge`, imported rather than reimplemented:
    // a `count: number` field on the primitive would mean a second copy of the cap and formatting, and
    // the phone would be the copy nobody notices drifting.
    const sectionItems: NavigationMenuItem[] = React.useMemo(() => {
        if (!sectionNavigation) {
            return [];
        }
        const items: NavigationMenuItem[] = sectionNavigation.sections.map((section) => ({
            id: `section-${section.value}`,
            icon: section.icon,
            text: section.label,
            // The page's own `buildSectionHref`, and it now REACHES THE DOM (`DISCUSSION-67`): the row
            // renders as an anchor, so long-press "Open in new tab" / "Copy link address" work and
            // assistive tech announces a link. A plain tap is still intercepted and routed through the
            // page's history-backed section handler, exactly as the strip's anchors do (rule 33).
            href: sectionNavigation.buildHref?.(section.value),
            current: section.value === sectionNavigation.activeSection,
            trailing: <SectionCountBadge count={section.count} active={section.value === sectionNavigation.activeSection} />,
            onSelect: () => sectionNavigation.onSelect(section.value),
        }));

        // `DISCUSSION-62` — the post itself, first in the list. No `href`: the article is a destination
        // on THIS page and has no URL of its own, and an anchor pointing at a URL that does not exist
        // would be the dishonest half of `DISCUSSION-67` restated. Existing copy only (rule 11):
        // `thread.back_to_discussion` already reads "Back to post" in all 24 catalogs and is what the
        // chain view's own link says.
        if (sectionNavigation.onBackToArticle) {
            items.unshift({
                id: "back-to-article",
                icon: ArrowUp,
                text: localizer("thread.back_to_discussion"),
                onSelect: sectionNavigation.onBackToArticle,
            });
        }

        return items;
    }, [localizer, sectionNavigation]);

    // The bar only exists on mobile; while a sheet/dialog is up it steps aside (the editor bottom sheet
    // owns the bottom edge then). SSR / no-DOM renders nothing.
    //
    // ⚠️ `keyboardInsetPx > 0` is the third condition and it was missing until 2026-09-05. A member
    // reported on 2026-08-27 that "la barra de las flechitas de me gusta o no se queda trabada sobre el
    // cuadro de texto y no me deja ver que escribo para responderle a alguien" -- the vote bar parked
    // over the reply box so they could not see what they were typing. The mechanism: this bar is
    // `fixed bottom-0`, which pins it to the LAYOUT viewport, and the soft keyboard does not shrink the
    // layout viewport -- only the visual one. So when the keyboard opens the bar stays where the
    // keyboard now is, on top of the composer. `modalOpen` did not save it, because
    // `DiscussionThoughtsDisplayer` renders the discussion-level composer INLINE with no `isMobile`
    // gate: no dialog opens, so `body.modal-open` is never set and the bar never steps aside.
    //
    // Hidden, not raised, and that is the deliberate half. Every other bottom-docked surface in the
    // product (`DiscussionTextEditor`, `DiscussionDraftPreviewCard`, `ChatExperience`,
    // `DirectMessageComposer`) consumes this hook as an OFFSET -- `style={{ bottom: keyboardInsetPx }}`
    // -- so it rides just above the keyboard. Copying that here is the obvious fix and it would
    // reproduce the complaint: those surfaces ARE the composer, so sitting above the keyboard is where
    // they belong, whereas this bar is the article's vote and section chrome, and "just above the
    // keyboard" is exactly the strip of screen the member is trying to read their own sentence in.
    // Nothing is lost by hiding it: `useBottomBarClearance` keeps the page's bottom padding so the
    // layout does not jump, and the hook's own focus invariant brings the bar back the moment focus
    // leaves the editor.
    //
    // Degrades to today's behaviour where it must: `readKeyboardInsetPx` returns 0 when there is no
    // `visualViewport`, so a browser that cannot report the keyboard keeps the bar rather than losing
    // it. That direction is the safe one.
    if (!isMobile || modalOpen || keyboardInsetPx > 0 || typeof document === "undefined") {
        return null;
    }

    const bar = (
        <div
            className="pointer-events-none fixed inset-x-0 bottom-0 z-layer-sticky flex justify-center px-3 pb-[env(safe-area-inset-bottom)]"
            data-playwright-id="discussion-mobile-action-bar"
            data-layout-monitor-ignore="true"
        >
            {/* `DISCUSSION-16` (wave 8, batch J) — the bar is PINNED to the bottom edge and floats over
                the article, so at 85% opacity a line of body text was legible INSIDE the control on
                every screen the reader scrolled past: in the light frame the words "understanding
                sources of assumptions and" read plainly through it, and in dark they smeared grey
                behind the icons. Neither hidden nor readable is the one state a frosted surface must
                not be in, and it degraded the legibility of the bar's own icons and counts too.

                `--background2` at full opacity, matching the composer bar's own fix
                (`CREATE-PUBLISH-3`): flat-X elevation is a real surface plus a border, never a
                shadow and never transparency standing in for one. The blur is kept for the edges the
                pill's own radius leaves, and nothing is permanently hidden by the bar being solid
                because `useBottomBarClearance` already reserves the space beneath it. */}
            <div className="pointer-events-auto mb-2 flex w-full max-w-lg items-center justify-between gap-2 rounded-full border border-border bg-[var(--background2)] px-3 py-1.5 shadow-[var(--surface-shadow-md)] backdrop-blur-sm">
                {/* `DISCUSSION-26` — the phone's way back into the section list, in the one piece of chrome
                    that does not scroll away. It costs no new vertical space (the bar already exists) and
                    no new copy: the trigger's accessible name is the `<nav>`'s own `ariaLabel`, and each
                    item is the section's own label from the shared builder.

                    A menu rather than a second scrolling strip, deliberately: the strip's horizontal
                    overflow is `DISCUSSION-25`'s defect, and reproducing it in a 44px-tall bar would put
                    the same seven entries behind the same invisible scroll. */}
                {sectionNavigation && sectionItems.length > 0 ? (
                    <>
                        <NavigationMenuDropdown
                            icon={LayoutList}
                            ariaLabel={sectionNavigation.ariaLabel}
                            playwrightId="discussion-mobile-bar-sections"
                            size="sm"
                            variant="ghost"
                            items={sectionItems}
                            // `DISCUSSION-56` — this trigger is the LEFTMOST control in a bar docked to
                            // the BOTTOM edge, which is the one anchor the card-overflow menu's
                            // hardcoded `side="bottom" align="end"` cannot serve: Radix flipped the menu
                            // upward and then shifted the end-aligned content until it collided with the
                            // viewport, landing its hairline in the leftmost pixel column — 0.0 CSS px
                            // of inset against 16.0 for the article card and 11.8 for this pill, with
                            // the border clipped by the display edge and sitting inside the system
                            // back-gesture strip, and the menu's bottom edge painting over the bar's own
                            // hairline. Stated at the call site because `align="end"` remains right for
                            // the `…` overflow menus the other component was built for.
                            //
                            // `collisionPadding` is 12 to match this bar's own `px-3` gutter, so the
                            // menu can never be flush to the display edge; `sideOffset` 8 keeps it clear
                            // of the pill's top border.
                            side="top"
                            align="start"
                            sideOffset={8}
                            collisionPadding={12}
                        />
                        {/* `DISCUSSION-70` — the one control in this bar that NAVIGATES, standing among
                            five that act on the post, and the bar expressed that category difference in
                            no way at all. A hairline is the whole statement: it costs no copy, no
                            vertical space and no tap target, and it is the same
                            border-not-shadow vocabulary the pill itself is drawn in (rule 23).
                            `aria-hidden`, because a separator is a visual grouping and the menu it
                            follows already carries the `<nav>`'s own accessible name. */}
                        <span aria-hidden="true" className="h-6 w-px shrink-0 self-center bg-border" />
                    </>
                ) : <span />}

                {props.showBookmark ? (
                    <CtaToggle
                        icon={Bookmark}
                        variant="ghost"
                        size="sm"
                        requiresLogin={true}
                        accountAccessDialog={props.saveAccountAccessDialog}
                        buttonActive={props.isSavedToBookmarks}
                        onPressedChange={() => props.onToggleBookmark()}
                        disabled={props.savingToBookmarks}
                        isLoading={props.savingToBookmarks}
                        interactionEffect="bookmark"
                        playwrightId="discussion-mobile-bar-bookmark"
                        title={localizer(props.isSavedToBookmarks ? "typescript.discussion.saved" : "typescript.discussion.save")}
                        // `DISCUSSION-70` — the SAME already-localized string, promoted from `title` to
                        // an explicit `ariaLabel`. `title` is the accname spec's last-resort tier and a
                        // tooltip a touch device never opens, so on the viewport this bar exists for it
                        // was the only name six controls had. No new key (rule 11).
                        ariaLabel={localizer(props.isSavedToBookmarks ? "typescript.discussion.saved" : "typescript.discussion.save")}
                        displayTextOnMobile={false}
                    />
                ) : <span />}

                {props.showShare ? (
                    <CtaButton
                        icon={Share}
                        variant="ghost"
                        size="sm"
                        displayTextOnMobile={false}
                        playwrightId="discussion-mobile-bar-share"
                        title={localizer("typescript.discussion.share")}
                        // `DISCUSSION-70`, same promotion as bookmark above — and this is one of the two
                        // controls the row singles out, because a bare share glyph next to an abstract
                        // list glyph is a name a member can only learn by tapping.
                        ariaLabel={localizer("typescript.discussion.share")}
                        // `DISCUSSION-58` — the pending half. Between the tap and the sheet the fallback
                        // path awaits a chunk; without this the bar looked like a button that did
                        // nothing, and a second tap produced a second sheet plus a duplicated
                        // `SHARE_INITIATED` row.
                        isLoading={props.sharePending ?? false}
                        onClick={(ev) => {
                            stopEvent(ev);
                            props.onShare();
                        }}
                    />
                ) : <span />}

                <VoteContainer
                    leftButtonIcon={ArrowBigUp}
                    leftButtonTitle={props.agreementTitle}
                    leftButtonCount={props.agreementsCount}
                    size="sm"
                    localizer={localizer}
                    leftButtonActive={props.isAgreeing}
                    displayTextOnMobile={false}
                    disabled={props.votesDisabled}
                    disabledReason={props.votesDisabledReason}
                    leftButtonExtraClass="agreement-discussion-button"
                    leftInteractionEffect="agree"
                    leftCountFeedback="pulse"
                    rightButtonIcon={ArrowBigDown}
                    rightButtonTitle={props.disagreementTitle}
                    rightButtonCount={props.disagreementsCount}
                    rightButtonActive={props.isDisagreeing}
                    rightButtonExtraClass="disagreement-discussion-button"
                    rightInteractionEffect="disagree"
                    rightCountFeedback="pulse"
                    requiresLogin={true}
                    accountAccessDialog={props.reactAccountAccessDialog}
                    onLeftClick={(ev) => {
                        stopEvent(ev);
                        props.onAgree();
                    }}
                    onRightClick={(ev) => {
                        stopEvent(ev);
                        props.onDisagree();
                    }}
                />

                {props.showComment ? (
                    <CtaButton
                        icon={MessageSquareText}
                        buttonCount={props.argumentsCount}
                        size="sm"
                        variant="ghost"
                        // SV-47: gate at Publish, not before writing.
                        requiresLogin={false}
                        disabled={props.commentDisabled}
                        disabledReason={props.commentDisabledReason}
                        displayTextOnMobile={false}
                        playwrightId="discussion-mobile-bar-comment"
                        title={props.commentTitle}
                        // `DISCUSSION-70` — same promotion. The count button already carries a
                        // caller-chosen, already-localized label; this makes it the accessible NAME
                        // rather than a tooltip nobody on a phone can open.
                        ariaLabel={props.commentTitle}
                        onClick={(ev) => {
                            stopEvent(ev);
                            props.onComment();
                        }}
                    />
                ) : <span />}
            </div>
        </div>
    );

    return createPortal(bar, document.body);
}
